import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  Users,
  CheckCircle2,
  Mail,
  Phone,
  Eye,
  Send,
  Plus,
  Search,
  Filter,
  ArrowRight,
  Briefcase,
  FileText,
  Calculator,
  Building,
  Calendar,
  Sparkles,
  RefreshCw,
  Award
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { dbHelpers } from '../../config/database';

interface InquiryLead {
  id: string | number;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject?: string;
  message?: string;
  status?: 'new' | 'contacted' | 'proposal' | 'signed' | string;
  created_at?: string;
}

export const SalesDashboard: React.FC = () => {
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState<InquiryLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<'pipeline' | 'inquiries' | 'estimator' | 'deals'>('pipeline');
  const [selectedLead, setSelectedLead] = useState<InquiryLead | null>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);

  // Estimator state
  const [estimator, setEstimator] = useState({
    clientName: '',
    serviceType: 'Web & Cloud App Development',
    hours: 120,
    ratePerHour: 1500,
    discountPercent: 5
  });
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  useEffect(() => {
    loadSalesData();
  }, []);

  const loadSalesData = async () => {
    setLoading(true);
    try {
      const res = await dbHelpers.getInquiries();
      if (res?.status && Array.isArray(res.data)) {
        setInquiries(res.data);
      } else {
        setInquiries([]);
      }
    } catch (err) {
      console.error('Failed to load inquiries for Sales Dashboard', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (leadId: string | number, newStatus: string) => {
    try {
      const res = await dbHelpers.updateInquiryStatus(leadId, newStatus);
      if (res?.status) {
        setInquiries(prev =>
          prev.map(item => (String(item.id) === String(leadId) ? { ...item, status: newStatus } : item))
        );
      } else {
        alert(res?.message || 'Failed to update lead status');
      }
    } catch (err) {
      console.error('Error updating status', err);
      alert('Network error while updating status');
    }
  };

  const filteredInquiries = inquiries.filter(item => {
    const q = searchQuery.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(q) ||
      (item.email || '').toLowerCase().includes(q) ||
      (item.subject || '').toLowerCase().includes(q) ||
      (item.company || '').toLowerCase().includes(q)
    );
  });

  // KPI Calculations
  const totalLeads = inquiries.length;
  const newLeads = inquiries.filter(i => (i.status || 'new').toLowerCase() === 'new').length;
  const contactedLeads = inquiries.filter(i => (i.status || '').toLowerCase() === 'contacted').length;
  const proposalLeads = inquiries.filter(i => (i.status || '').toLowerCase() === 'proposal').length;
  const signedDeals = inquiries.filter(i => (i.status || '').toLowerCase() === 'signed').length;
  const winRate = totalLeads > 0 ? Math.round((signedDeals / totalLeads) * 100) : 0;
  const estimatedPipelineValue = (proposalLeads * 180000) + (signedDeals * 350000) + (newLeads * 90000);

  // Estimator Calculations
  const grossTotal = estimator.hours * estimator.ratePerHour;
  const discountAmount = (grossTotal * estimator.discountPercent) / 100;
  const netEstimatedTotal = grossTotal - discountAmount;

  return (
    <div className="flex-1 bg-slate-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header Bar */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" />
                Sales & Revenue Hub
              </span>
              <span className="text-xs text-slate-500">Live CRM Pipeline</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              Sales Team Dashboard
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Welcome back, <span className="font-semibold text-slate-800">{user?.name || 'Sales Representative'}</span>. Track client inquiries, manage deals, and close contracts.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadSalesData}
              disabled={loading}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-all flex items-center gap-2 border border-slate-300 shadow-sm cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh Leads
            </button>
            <button
              onClick={() => setActiveSubTab('estimator')}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              New Quotation
            </button>
          </div>
        </div>

        {/* Sales KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Total Pipeline Leads</span>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900">{totalLeads}</div>
            <p className="text-xs text-blue-600 font-medium mt-1">Active client prospects</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">New Inquiries</span>
              <Mail className="w-5 h-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-amber-600">{newLeads}</div>
            <p className="text-xs text-amber-700 font-medium mt-1">Awaiting discovery call</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Proposals Sent</span>
              <FileText className="w-5 h-5 text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-purple-600">{proposalLeads}</div>
            <p className="text-xs text-purple-700 font-medium mt-1">In final negotiation</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Closed Won / Signed</span>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-emerald-600">{signedDeals}</div>
            <p className="text-xs text-emerald-700 font-medium mt-1">{winRate}% conversion win rate</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-emerald-100 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Est. Pipeline Value</span>
              <TrendingUp className="w-5 h-5 text-emerald-200" />
            </div>
            <div className="text-2xl font-bold">₹{(estimatedPipelineValue / 100000).toFixed(1)}L</div>
            <p className="text-xs text-emerald-100 font-medium mt-1">Total deal forecast</p>
          </div>
        </div>

        {/* Tab Navigation & Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200 px-6 pt-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex space-x-6">
              <button
                onClick={() => setActiveSubTab('pipeline')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                  activeSubTab === 'pipeline'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                Pipeline Kanban
                <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">
                  {inquiries.length}
                </span>
              </button>

              <button
                onClick={() => setActiveSubTab('inquiries')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                  activeSubTab === 'inquiries'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Mail className="w-4 h-4" />
                Inquiries Feed
                {newLeads > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                    {newLeads}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveSubTab('estimator')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                  activeSubTab === 'estimator'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Calculator className="w-4 h-4" />
                Quotation Calculator
              </button>

              <button
                onClick={() => setActiveSubTab('deals')}
                className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                  activeSubTab === 'deals'
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Award className="w-4 h-4" />
                Won Accounts
                <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-full font-semibold">
                  {signedDeals}
                </span>
              </button>
            </div>

            {/* Search Input */}
            <div className="pb-4">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search lead or client..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-slate-50"
                />
              </div>
            </div>
          </div>

          {/* Sub-Tab 1: Interactive Kanban Pipeline */}
          {activeSubTab === 'pipeline' && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  {
                    key: 'new',
                    label: '1. New Leads',
                    badgeColor: 'bg-blue-100 text-blue-800 border-blue-200'
                  },
                  {
                    key: 'contacted',
                    label: '2. Contacted / Discovery',
                    badgeColor: 'bg-amber-100 text-amber-800 border-amber-200'
                  },
                  {
                    key: 'proposal',
                    label: '3. Proposal Sent',
                    badgeColor: 'bg-purple-100 text-purple-800 border-purple-200'
                  },
                  {
                    key: 'signed',
                    label: '4. Closed Won / Signed',
                    badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200'
                  }
                ].map(col => {
                  const colLeads = filteredInquiries.filter(
                    lead => (lead.status || 'new').toLowerCase() === col.key
                  );

                  return (
                    <div
                      key={col.key}
                      className="bg-slate-100/70 rounded-2xl p-4 flex flex-col min-h-[550px] border border-slate-200"
                    >
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${col.badgeColor}`}>
                          {col.label}
                        </span>
                        <span className="text-xs font-bold text-slate-600 bg-white px-2.5 py-0.5 rounded-full shadow-xs border border-slate-200">
                          {colLeads.length}
                        </span>
                      </div>

                      <div className="flex-1 space-y-3 overflow-y-auto max-h-[620px] pr-1">
                        {colLeads.length === 0 ? (
                          <div className="text-center py-12 text-slate-400 text-xs italic border-2 border-dashed border-slate-200 rounded-xl bg-white/40">
                            No leads in this stage
                          </div>
                        ) : (
                          colLeads.map(lead => (
                            <div
                              key={lead.id}
                              className="bg-white p-4 rounded-xl shadow-xs border border-slate-200 hover:shadow-md transition-all space-y-3 relative group"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-bold text-sm text-slate-900">{lead.name}</h4>
                                  <p className="text-xs text-slate-500 font-medium">
                                    {lead.company || 'Direct Client'}
                                  </p>
                                </div>
                                <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                                  #{lead.id}
                                </span>
                              </div>

                              <div className="space-y-1 text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                                <div className="flex items-center gap-1.5 truncate">
                                  <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  <span className="truncate">{lead.email}</span>
                                </div>
                                {lead.phone && (
                                  <div className="flex items-center gap-1.5">
                                    <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                    <span>{lead.phone}</span>
                                  </div>
                                )}
                                <div className="text-slate-700 font-semibold truncate pt-1 border-t border-slate-200/60">
                                  {lead.subject || 'General IT Inquiry'}
                                </div>
                              </div>

                              {lead.message && (
                                <p className="text-xs text-slate-500 line-clamp-2 italic">
                                  "{lead.message}"
                                </p>
                              )}

                              <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedLead(lead);
                                    setShowLeadModal(true);
                                  }}
                                  className="text-xs text-slate-600 hover:text-emerald-700 font-semibold flex items-center gap-1 transition-colors cursor-pointer"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  Details
                                </button>

                                {/* Stage Selector */}
                                <select
                                  value={(lead.status || 'new').toLowerCase()}
                                  onChange={e => handleStageChange(lead.id, e.target.value)}
                                  className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                                >
                                  <option value="new">New Lead</option>
                                  <option value="contacted">Contacted</option>
                                  <option value="proposal">Proposal</option>
                                  <option value="signed">Closed Won</option>
                                </select>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sub-Tab 2: Inquiries Feed Table */}
          {activeSubTab === 'inquiries' && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">ID</th>
                      <th className="py-3 px-4">Contact Person</th>
                      <th className="py-3 px-4">Subject & Interest</th>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Pipeline Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredInquiries.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-10 text-slate-400">
                          No inquiries found.
                        </td>
                      </tr>
                    ) : (
                      filteredInquiries.map(item => (
                        <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-4 font-mono text-xs text-slate-400">#{item.id}</td>
                          <td className="py-3.5 px-4">
                            <div className="font-semibold text-slate-900">{item.name}</div>
                            <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                              <span>{item.email}</span>
                              {item.phone && <span>• {item.phone}</span>}
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="font-medium text-slate-800">{item.subject || 'General Inquiry'}</div>
                            {item.company && (
                              <div className="text-xs text-slate-400">Company: {item.company}</div>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-slate-500">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Recent'}
                          </td>
                          <td className="py-3.5 px-4">
                            <select
                              value={(item.status || 'new').toLowerCase()}
                              onChange={e => handleStageChange(item.id, e.target.value)}
                              className={`text-xs px-2.5 py-1 rounded-full font-bold border cursor-pointer ${
                                item.status === 'signed'
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                  : item.status === 'proposal'
                                  ? 'bg-purple-100 text-purple-800 border-purple-300'
                                  : item.status === 'contacted'
                                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                                  : 'bg-blue-100 text-blue-800 border-blue-300'
                              }`}
                            >
                              <option value="new">New Lead</option>
                              <option value="contacted">Contacted</option>
                              <option value="proposal">Proposal Sent</option>
                              <option value="signed">Closed Won</option>
                            </select>
                          </td>
                          <td className="py-3.5 px-4 text-right space-x-2">
                            <button
                              onClick={() => {
                                setSelectedLead(item);
                                setShowLeadModal(true);
                              }}
                              className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors inline-flex items-center gap-1 cursor-pointer"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>
                            <a
                              href={`mailto:${item.email}?subject=X2 InfoTech - Following up on your inquiry: ${item.subject || ''}`}
                              className="px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-lg transition-colors inline-flex items-center gap-1"
                            >
                              <Send className="w-3.5 h-3.5" />
                              Reply
                            </a>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-Tab 3: Quotation & Proposal Estimator */}
          {activeSubTab === 'estimator' && (
            <div className="p-6 max-w-4xl mx-auto space-y-6">
              <div className="bg-emerald-50/60 border border-emerald-200 p-4 rounded-xl flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <p className="text-xs text-emerald-800">
                  Quickly generate commercial project estimates and scope pricing for client proposals. Figures update dynamically in real time.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
                <div className="space-y-4">
                  <h3 className="font-bold text-slate-900 text-base">Client & Scope Parameters</h3>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Client Name / Organization</label>
                    <input
                      type="text"
                      placeholder="e.g. Apex Global Tech Pvt Ltd"
                      value={estimator.clientName}
                      onChange={e => setEstimator({ ...estimator, clientName: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Service & Solution Package</label>
                    <select
                      value={estimator.serviceType}
                      onChange={e => setEstimator({ ...estimator, serviceType: e.target.value })}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                    >
                      <option value="Web & Cloud App Development">Custom Web & Cloud App Development</option>
                      <option value="Mobile App (iOS & Android)">Mobile App Engineering (React Native / Flutter)</option>
                      <option value="Enterprise ERP & CRM">Enterprise Software, ERP & Microservices</option>
                      <option value="AI & Machine Learning Solutions">AI Integration & Intelligent Automation</option>
                      <option value="Cloud DevOps & Migration">Cloud Migration & AWS Infrastructure</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Estimated Hours</label>
                      <input
                        type="number"
                        min="10"
                        value={estimator.hours}
                        onChange={e => setEstimator({ ...estimator, hours: Number(e.target.value) || 0 })}
                        className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Billing Rate (₹/hr)</label>
                      <input
                        type="number"
                        min="500"
                        step="100"
                        value={estimator.ratePerHour}
                        onChange={e => setEstimator({ ...estimator, ratePerHour: Number(e.target.value) || 0 })}
                        className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                      Promotional Discount ({estimator.discountPercent}%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="25"
                      step="1"
                      value={estimator.discountPercent}
                      onChange={e => setEstimator({ ...estimator, discountPercent: Number(e.target.value) })}
                      className="w-full accent-emerald-600"
                    />
                  </div>
                </div>

                {/* Estimate Summary Box */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm mb-4 pb-2 border-b border-slate-200">
                      Commercial Estimate Breakdown
                    </h4>

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between text-slate-600">
                        <span>Base Scope ({estimator.hours} hrs @ ₹{estimator.ratePerHour}/hr):</span>
                        <span className="font-semibold text-slate-900">₹{grossTotal.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-emerald-700">
                        <span>Discount ({estimator.discountPercent}%):</span>
                        <span className="font-semibold">-₹{discountAmount.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>GST (18% Intra-state):</span>
                        <span className="font-semibold text-slate-900">₹{Math.round(netEstimatedTotal * 0.18).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="pt-3 border-t border-slate-200 flex justify-between items-center">
                        <span className="font-bold text-slate-900 text-base">Net Quote (Incl. GST):</span>
                        <span className="font-extrabold text-2xl text-emerald-700">
                          ₹{Math.round(netEstimatedTotal * 1.18).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6">
                    <button
                      onClick={() => {
                        setQuoteSuccess(true);
                        setTimeout(() => setQuoteSuccess(false), 4000);
                      }}
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 text-sm cursor-pointer"
                    >
                      <Send className="w-4 h-4" />
                      Save & Copy Quotation Summary
                    </button>
                    {quoteSuccess && (
                      <div className="mt-2 text-center text-xs font-semibold text-emerald-600 animate-fade-in">
                        ✓ Quotation recorded successfully! Ready to dispatch to client.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sub-Tab 4: Won Accounts */}
          {activeSubTab === 'deals' && (
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900 text-base">Closed Won Clients & Signed Contracts</h3>
                  <span className="text-xs text-slate-500">Total: {signedDeals} client accounts</span>
                </div>

                {filteredInquiries.filter(i => (i.status || '').toLowerCase() === 'signed').length === 0 ? (
                  <div className="text-center py-12 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    No deals marked as Closed Won yet. Move leads in the Kanban to "Closed Won" as you win deals!
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredInquiries
                      .filter(i => (i.status || '').toLowerCase() === 'signed')
                      .map(deal => (
                        <div
                          key={deal.id}
                          className="bg-white p-5 rounded-xl border border-emerald-200 shadow-xs hover:shadow-md transition-all space-y-3"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold text-slate-900 text-base">{deal.name}</h4>
                              <p className="text-xs text-slate-500 font-medium">{deal.company || 'Enterprise Client'}</p>
                            </div>
                            <span className="bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Signed
                            </span>
                          </div>

                          <div className="text-xs text-slate-600 bg-emerald-50/50 p-3 rounded-lg border border-emerald-100 space-y-1">
                            <div><span className="font-semibold text-slate-700">Service:</span> {deal.subject}</div>
                            <div><span className="font-semibold text-slate-700">Email:</span> {deal.email}</div>
                            {deal.phone && <div><span className="font-semibold text-slate-700">Phone:</span> {deal.phone}</div>}
                          </div>

                          <div className="pt-2 flex justify-between items-center text-xs">
                            <span className="text-slate-400 font-medium">Status: Active Contract</span>
                            <a
                              href="#billing"
                              className="text-emerald-700 font-bold hover:underline flex items-center gap-1"
                            >
                              Generate Bill <ArrowRight className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lead Detail Modal */}
      {showLeadModal && selectedLead && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 border border-slate-200">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Lead Record #{selectedLead.id}</span>
                <h3 className="text-lg font-bold text-slate-900">{selectedLead.name}</h3>
                <p className="text-xs text-slate-500">{selectedLead.company || 'Direct Client'}</p>
              </div>
              <button
                onClick={() => setShowLeadModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-100 space-y-1.5 text-xs text-slate-600">
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-700">Email:</span>
                  <a href={`mailto:${selectedLead.email}`} className="text-emerald-700 font-medium underline">
                    {selectedLead.email}
                  </a>
                </div>
                {selectedLead.phone && (
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-700">Phone:</span>
                    <a href={`tel:${selectedLead.phone}`} className="text-emerald-700 font-medium underline">
                      {selectedLead.phone}
                    </a>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-700">Service Interest:</span>
                  <span className="font-medium text-slate-900">{selectedLead.subject || 'General'}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Message from Customer:</label>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {selectedLead.message || 'No additional message text.'}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Pipeline Stage:</label>
                <select
                  value={(selectedLead.status || 'new').toLowerCase()}
                  onChange={e => {
                    handleStageChange(selectedLead.id, e.target.value);
                    setSelectedLead({ ...selectedLead, status: e.target.value });
                  }}
                  className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white cursor-pointer"
                >
                  <option value="new">New Lead</option>
                  <option value="contacted">Contacted / Discovery</option>
                  <option value="proposal">Proposal Sent</option>
                  <option value="signed">Closed Won / Signed</option>
                </select>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
              <a
                href={`mailto:${selectedLead.email}?subject=X2 InfoTech Consultation - ${selectedLead.subject || ''}`}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
              >
                <Send className="w-3.5 h-3.5" />
                Reply via Email
              </a>
              <button
                onClick={() => setShowLeadModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default SalesDashboard;
